import { create } from 'zustand';

import { useDeviceSyncStore } from 'src/store/DeviceSync';
import { useDeviceContextStore } from 'src/store/DeviceContext';

import type {
  OperatorSessionContext,
  ClearOperatorSessionOptions,
  OperatorAuthenticationState,
  OperatorSessionExpirationMode,
  OperatorAuthenticationLockReason,
  SetAuthenticatedOperatorSessionInput,
} from './types';

export * from './types';
export * from './demand';

export const OPERATOR_SESSION_HEADER = 'X-PointCraft-Operator-Session' as const;
export const OPERATOR_ACTIVITY_IGNORE_ATTRIBUTE = 'data-operator-activity-ignore' as const;

const INITIAL_STATE: OperatorAuthenticationState = {
  status: 'INITIALIZING',
  operator: null,
  expirationMode: null,
  expiresAt: null,
  lastActivityAt: null,
  context: null,
  lockReason: null,
  errorCode: null,
  message: null,
};

// The raw credential intentionally lives outside Zustand state. This keeps it
// out of persistence, React snapshots, devtools, and diagnostic store exports.
let rawOperatorSessionToken: string | null = null;

function normalizeNullableString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized || null;
}

function normalizeContext(context: OperatorSessionContext): OperatorSessionContext {
  return {
    businessId: normalizeNullableString(context.businessId),
    businessDeviceId: normalizeNullableString(context.businessDeviceId),
    terminalId: normalizeNullableString(context.terminalId),
  };
}

function replaceControlCharacters(value: string): string {
  return value
    .split('')
    .map((character) => {
      const code = character.charCodeAt(0);
      return code <= 31 || code === 127 ? ' ' : character;
    })
    .join('');
}

function normalizeSafeMessage(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const normalized = replaceControlCharacters(value).trim();
  return normalized ? normalized.slice(0, 300) : null;
}

function contextMismatchReason(
  expected: OperatorSessionContext,
  current: Partial<OperatorSessionContext>
): OperatorAuthenticationLockReason | null {
  if (
    Object.prototype.hasOwnProperty.call(current, 'businessId') &&
    expected.businessId !== normalizeNullableString(current.businessId)
  ) {
    return 'BUSINESS_CHANGED';
  }
  if (
    Object.prototype.hasOwnProperty.call(current, 'businessDeviceId') &&
    expected.businessDeviceId !== normalizeNullableString(current.businessDeviceId)
  ) {
    return 'DEVICE_CHANGED';
  }
  if (
    Object.prototype.hasOwnProperty.call(current, 'terminalId') &&
    expected.terminalId !== normalizeNullableString(current.terminalId)
  ) {
    return 'TERMINAL_CHANGED';
  }
  return null;
}

export const useOperatorAuthenticationStore = create<OperatorAuthenticationState>(() => ({
  ...INITIAL_STATE,
}));

export function getCurrentOperatorSessionContext(): OperatorSessionContext {
  const deviceContext = useDeviceContextStore.getState();
  const syncedBusinessId = useDeviceSyncStore.getState().businessData?.id;

  return normalizeContext({
    businessId: syncedBusinessId ?? deviceContext.businessId,
    businessDeviceId: deviceContext.businessDeviceId,
    terminalId: deviceContext.terminalId,
  });
}

/**
 * Atomically enters the submitting state and drops any prior local session.
 * Returns false when another authentication submission is already active.
 */
export function markOperatorAuthenticating(): boolean {
  const state = useOperatorAuthenticationStore.getState();
  if (state.status === 'AUTHENTICATING') return false;

  rawOperatorSessionToken = null;
  useOperatorAuthenticationStore.setState({
    status: 'AUTHENTICATING',
    operator: null,
    expirationMode: null,
    expiresAt: null,
    lastActivityAt: null,
    context: null,
    lockReason: null,
    errorCode: null,
    message: null,
  });
  return true;
}

/**
 * Stores safe session metadata in Zustand and the opaque token in module memory.
 * Invalid or already-expired responses fail closed and return false.
 */
export function setAuthenticatedOperatorSession(
  input: SetAuthenticatedOperatorSessionInput,
  roleName?: string | null
): boolean {
  const token = normalizeNullableString(input.operatorSessionToken);
  const operatorId = normalizeNullableString(input.operator.id);
  const operatorName = normalizeNullableString(input.operator.name);
  const expirationMode = input.expirationMode ?? 'INACTIVITY';
  const expiresAtMs = typeof input.expiresAt === 'string' ? Date.parse(input.expiresAt) : Number.NaN;

  if (
    !token ||
    !operatorId ||
    !operatorName ||
    (expirationMode === 'INACTIVITY' && !Number.isFinite(expiresAtMs)) ||
    (expirationMode === 'MANUAL' && input.expiresAt !== null)
  ) {
    clearOperatorSession({
      reason: 'AUTHENTICATION_UNAVAILABLE',
      errorCode: 'OPERATOR_AUTHENTICATION_UNAVAILABLE',
      message: 'Operator authentication returned an invalid session.',
    });
    return false;
  }

  if (expirationMode === 'INACTIVITY' && expiresAtMs <= Date.now()) {
    clearOperatorSession({
      reason: 'EXPIRED',
      errorCode: 'OPERATOR_SESSION_EXPIRED',
      message: 'Your operator session has expired. Please sign in again.',
    });
    return false;
  }

  const normalizedRoleName = normalizeNullableString(roleName ?? input.operator.roleName);
  rawOperatorSessionToken = token;
  useOperatorAuthenticationStore.setState({
    status: 'AUTHENTICATED',
    operator: {
      id: operatorId,
      name: operatorName,
      roleId: normalizeNullableString(input.operator.roleId),
      roleName: normalizedRoleName,
    },
    expirationMode,
    expiresAt: expirationMode === 'INACTIVITY' ? new Date(expiresAtMs).toISOString() : null,
    lastActivityAt: Date.now(),
    context: normalizeContext(input.context ?? getCurrentOperatorSessionContext()),
    lockReason: null,
    errorCode: null,
    message: null,
  });
  return true;
}

/**
 * Clears the opaque token and safe metadata. Repeating the same clear is a no-op.
 */
export function clearOperatorSession(options: ClearOperatorSessionOptions = {}): boolean {
  const status = options.status ?? 'LOCKED';
  const lockReason = options.reason ?? (status === 'LOCKED' ? 'REQUIRED' : null);
  const errorCode = normalizeNullableString(options.errorCode);
  const message = normalizeSafeMessage(options.message);
  const state = useOperatorAuthenticationStore.getState();
  const alreadyCleared =
    rawOperatorSessionToken === null &&
    state.status === status &&
    state.operator === null &&
    state.expirationMode === null &&
    state.expiresAt === null &&
    state.lastActivityAt === null &&
    state.context === null &&
    state.lockReason === lockReason &&
    state.errorCode === errorCode &&
    state.message === message;

  rawOperatorSessionToken = null;
  if (alreadyCleared) return false;

  useOperatorAuthenticationStore.setState({
    status,
    operator: null,
    expirationMode: null,
    expiresAt: null,
    lastActivityAt: null,
    context: null,
    lockReason,
    errorCode,
    message,
  });
  return true;
}

export function resetOperatorAuthenticationState(): void {
  rawOperatorSessionToken = null;
  useOperatorAuthenticationStore.setState({ ...INITIAL_STATE }, true);
}

/** Records safe, memory-only metadata for meaningful local operator input. */
export function recordOperatorActivity(activityAt = Date.now()): void {
  const state = useOperatorAuthenticationStore.getState();
  if (state.status !== 'AUTHENTICATED' || !Number.isFinite(activityAt) || activityAt <= 0) return;

  useOperatorAuthenticationStore.setState({
    lastActivityAt: Math.min(Math.floor(activityAt), Date.now()),
  });
}

/**
 * Accepts backend-issued expiration metadata without replacing the operator,
 * bound context, or module-private credential. Stale responses after a local
 * lock are ignored; malformed or mode-changing responses fail closed.
 */
export function refreshSessionExpiration(
  expirationMode: OperatorSessionExpirationMode,
  expiresAt: string | null
): void {
  const state = useOperatorAuthenticationStore.getState();
  if (state.status !== 'AUTHENTICATED') return;

  if (!rawOperatorSessionToken || !state.operator || !state.context) {
    clearOperatorSession({
      reason: 'SESSION_INVALID',
      errorCode: 'OPERATOR_SESSION_INVALID',
      message: 'Your operator session is no longer valid. Please sign in again.',
    });
    return;
  }

  if (state.expirationMode !== expirationMode) {
    clearOperatorSession({
      reason: 'SESSION_INVALID',
      errorCode: 'OPERATOR_SESSION_INVALID',
      message: 'The operator session policy changed. Please sign in again.',
    });
    return;
  }

  if (expirationMode === 'MANUAL') {
    if (expiresAt !== null) {
      clearOperatorSession({
        reason: 'SESSION_INVALID',
        errorCode: 'OPERATOR_SESSION_INVALID',
        message: 'Operator authentication returned an invalid session.',
      });
    }
    return;
  }

  const expiresAtMs = typeof expiresAt === 'string' ? Date.parse(expiresAt) : Number.NaN;
  if (!Number.isFinite(expiresAtMs)) {
    clearOperatorSession({
      reason: 'SESSION_INVALID',
      errorCode: 'OPERATOR_SESSION_INVALID',
      message: 'Operator authentication returned an invalid session.',
    });
    return;
  }

  if (expiresAtMs <= Date.now()) {
    clearOperatorSession({
      reason: 'EXPIRED',
      errorCode: 'OPERATOR_SESSION_EXPIRED',
      message: 'Your operator session has expired. Please sign in again.',
    });
    return;
  }

  useOperatorAuthenticationStore.setState({ expiresAt: new Date(expiresAtMs).toISOString() });
}

/**
 * Returns the credential only while its authoritative expiry and optional context
 * binding remain valid. Any failure synchronously relocks before returning.
 */
export function getValidOperatorSessionTokenForRequest(
  context?: Partial<OperatorSessionContext>
): string | null {
  const state = useOperatorAuthenticationStore.getState();
  if (state.status !== 'AUTHENTICATED') return null;

  if (
    !rawOperatorSessionToken ||
    !state.expirationMode ||
    !state.operator ||
    !state.context ||
    (state.expirationMode === 'INACTIVITY' && !state.expiresAt) ||
    (state.expirationMode === 'MANUAL' && state.expiresAt !== null)
  ) {
    clearOperatorSession({
      reason: 'SESSION_INVALID',
      errorCode: 'OPERATOR_SESSION_INVALID',
      message: 'Your operator session is no longer valid. Please sign in again.',
    });
    return null;
  }

  if (state.expirationMode === 'INACTIVITY') {
    const expiresAtMs = Date.parse(state.expiresAt!);
    if (!Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now()) {
      clearOperatorSession({
        reason: 'EXPIRED',
        errorCode: 'OPERATOR_SESSION_EXPIRED',
        message: 'Your operator session has expired. Please sign in again.',
      });
      return null;
    }
  }

  const currentContext = context ?? getCurrentOperatorSessionContext();
  const mismatchReason = state.context
    ? contextMismatchReason(state.context, currentContext)
    : 'SESSION_CONTEXT_MISMATCH';

  if (mismatchReason) {
    clearOperatorSession({
      reason: mismatchReason,
      errorCode: 'OPERATOR_SESSION_CONTEXT_MISMATCH',
      message: 'The device context changed. Please sign in again.',
    });
    return null;
  }

  return rawOperatorSessionToken;
}
